import { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Paper,
  FormControl,
  FormLabel,
  FormHelperText,
  Box,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import NumberInput from 'components/NumberInput';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';

import httpClient from 'utils/httpClient';
import InterestPreview from './InterestPreview';
import RevertToDraftDialog from '../milestones/RevertToDraftDialog';
import SowAddendumRequiredDialog from '../milestones/SowAddendumRequiredDialog';
import SowAddendumFormDialog from '../milestones/SowAddendumFormDialog';

const DEFAULT_ESTIMATED_TOTAL_HOURS = 178;
const MAX_ESTIMATED_TOTAL_HOURS = 24 * 90;
const MAX_HOURS_PER_DAY = 24;

const createSowEditSchema = (isInternational) => {
  return yup.object().shape({
    markup_percent: yup
      .number()
      .typeError('Markup percent must be a number')
      .required('Markup percent is required')
      .min(0, 'Markup percent must be at least 0%')
      .max(200, 'Markup percent must be at most 200%')
      .test(
        'two-decimal-places',
        'Markup percent must have at most 2 decimal places',
        (value) => {
          if (value === undefined || value === null || value === '')
            return false;
          return /^\d+(\.\d{1,2})?$/.test(value.toString());
        }
      ),
    fx_rate: isInternational
      ? yup
          .number()
          .typeError('Forex rate must be a number')
          .required('Forex rate is required for international companies')
          .min(0.01, 'Forex rate must be greater than 0')
          .test(
            'two-decimal-places',
            'Forex rate must have at most 2 decimal places',
            (value) => {
              if (value === undefined || value === null || value === '')
                return false;
              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
          )
      : yup.mixed().notRequired(),
    estimated_total_hours: yup.lazy(() =>
      yup.mixed().when('$isHourlyBudgetModel', {
        is: true,
        then: () =>
          yup
            .number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null ? undefined : value
            )
            .typeError('Estimated hours must be a number')
            .min(1, 'Estimated hours must be at least 1')
            .max(
              MAX_ESTIMATED_TOTAL_HOURS,
              `Estimated hours cannot exceed ${MAX_ESTIMATED_TOTAL_HOURS}`
            )
            .integer('Estimated hours must be a whole number')
            .notRequired(),
        otherwise: () => yup.mixed().notRequired(),
      })
    ),
    hours_per_day: yup.lazy(() =>
      yup.mixed().when('$isHourlyBudgetModel', {
        is: true,
        then: () =>
          yup
            .number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null ? undefined : value
            )
            .typeError('Hours per day must be a number')
            .min(1, 'Hours per day must be at least 1')
            .max(MAX_HOURS_PER_DAY, 'Hours per day cannot exceed 24')
            .notRequired(),
        otherwise: () => yup.mixed().notRequired(),
      })
    ),
  });
};

const SOWEditForm = ({
  sow,
  onClose,
  onSuccess,
  onSowUpdate,
  onDocumentsReset,
}) => {
  const [loading, setLoading] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [addendumRequiredOpen, setAddendumRequiredOpen] = useState(false);
  const [addendumFormOpen, setAddendumFormOpen] = useState(false);

  // Check margin_segment from nested company object or flat structure
  const isInternational =
    sow?.company?.margin_segment === 'INTERNATIONAL' ||
    sow?.margin_segment === 'INTERNATIONAL';
  const isHourlyBudgetModel =
    sow?.gig?.budget_model === 'hourly' || sow?.budget_model === 'hourly';

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: yupResolver(createSowEditSchema(isInternational), {
      context: { isHourlyBudgetModel },
    }),
    defaultValues: {
      markup_percent: sow?.markup_percent ?? '',
      fx_rate: isInternational ? sow?.fx_rate ?? 86 : '',
      estimated_total_hours: isHourlyBudgetModel
        ? sow?.estimated_total_hours ?? DEFAULT_ESTIMATED_TOTAL_HOURS
        : '',
      hours_per_day: isHourlyBudgetModel
        ? sow?.hours_per_day ?? sow?.freelancer?.available_hours_per_day ?? ''
        : '',
    },
  });

  const rawMarkupPercent = watch('markup_percent');
  const fxRate = watch('fx_rate');
  const estimatedTotalHours = watch('estimated_total_hours');
  const hoursPerDay = watch('hours_per_day');
  const markupPercentValue =
    rawMarkupPercent === '' || rawMarkupPercent === null
      ? undefined
      : Number(rawMarkupPercent);

  // Transform SOW data into interest format for InterestPreview
  // Use nested objects from SOW response if available, otherwise fallback to metadata/flat structure
  const selectedInterest = useMemo(() => {
    if (!sow) return null;

    // Use nested objects if available (new API response format)
    const gig = sow?.gig;
    const company = sow?.company;
    const freelancer = sow?.freelancer;

    return {
      gig: {
        title:
          gig?.title || sow?.metadata?.gig_title || sow?.gig_title || 'N/A',
        job_role:
          gig?.job_role ||
          sow?.metadata?.freelancer_role ||
          sow?.freelancer_role ||
          'N/A',
        status: gig?.status || sow?.gig_status || sow?.status || null,
        job_type:
          gig?.job_type || sow?.metadata?.job_type || sow?.job_type || null,
        gig_type: gig?.gig_type || sow?.gig_type || null,
        budget_model: gig?.budget_model || sow?.budget_model || null,
        min_hourly_rate:
          gig?.min_hourly_rate ||
          sow?.metadata?.min_hourly_rate ||
          sow?.min_hourly_rate ||
          null,
        max_hourly_rate:
          gig?.max_hourly_rate ||
          sow?.metadata?.max_hourly_rate ||
          sow?.max_hourly_rate ||
          null,
        min_budget:
          gig?.min_budget ||
          sow?.metadata?.min_budget ||
          sow?.min_budget ||
          null,
        max_budget:
          gig?.max_budget ||
          sow?.metadata?.max_budget ||
          sow?.max_budget ||
          null,
      },
      company: {
        company_name: company?.company_name || sow?.company_legal_name || 'N/A',
        margin_segment: company?.margin_segment || sow?.margin_segment || 'N/A',
        client_rate:
          company?.client_rate ||
          sow?.client_rate ||
          sow?.metadata?.client_rate ||
          null,
        markup_percent: company?.markup_percent || sow?.markup_percent || null,
      },
      freelancer: {
        name: freelancer?.name || sow?.freelancer_name || 'N/A',
        email:
          freelancer?.email ||
          sow?.freelancer_email ||
          sow?.metadata?.freelancer_email ||
          null,
        freelancer_role:
          freelancer?.freelancer_role ||
          sow?.metadata?.freelancer_role ||
          sow?.freelancer_role ||
          null,
        profile_picture: freelancer?.profile_picture || null,
        freelancer_rate:
          freelancer?.freelancer_rate ||
          sow?.metadata?.freelancer_rate ||
          sow?.freelancer_rate ||
          null,
        available_hours_per_day:
          freelancer?.available_hours_per_day ||
          sow?.metadata?.available_hours_per_day ||
          sow?.hours_per_day ||
          null,
      },
    };
  }, [sow]);
  const freelancerDefaultHoursPerDay =
    selectedInterest?.freelancer?.available_hours_per_day ?? '';

  // Only set default fx_rate on initial load, not when user clears it
  useEffect(() => {
    if (isInternational && !dirtyFields?.fx_rate && !fxRate) {
      setValue('fx_rate', sow?.fx_rate ?? 86, { shouldValidate: true });
    } else if (!isInternational) {
      setValue('fx_rate', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
      clearErrors('fx_rate');
    }
  }, [
    isInternational,
    sow?.fx_rate,
    setValue,
    clearErrors,
    fxRate,
    dirtyFields,
  ]);

  useEffect(() => {
    if (!isHourlyBudgetModel) {
      setValue('estimated_total_hours', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('hours_per_day', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
      clearErrors('estimated_total_hours');
      clearErrors('hours_per_day');
      return;
    }

    if (
      !dirtyFields?.estimated_total_hours &&
      (estimatedTotalHours === '' ||
        estimatedTotalHours === null ||
        estimatedTotalHours === undefined)
    ) {
      setValue(
        'estimated_total_hours',
        sow?.estimated_total_hours ?? DEFAULT_ESTIMATED_TOTAL_HOURS,
        {
          shouldValidate: false,
          shouldDirty: false,
        }
      );
    }

    if (
      !dirtyFields?.hours_per_day &&
      (hoursPerDay === '' || hoursPerDay === null || hoursPerDay === undefined)
    ) {
      setValue('hours_per_day', freelancerDefaultHoursPerDay, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [
    clearErrors,
    dirtyFields?.estimated_total_hours,
    dirtyFields?.hours_per_day,
    estimatedTotalHours,
    freelancerDefaultHoursPerDay,
    hoursPerDay,
    isHourlyBudgetModel,
    setValue,
    sow?.estimated_total_hours,
  ]);

  const buildPayload = (data) => {
    const payload = {};

    const selectedMarkup = Number(data.markup_percent);
    if (!Number.isNaN(selectedMarkup)) {
      // Ensure 2 decimal places for markup percent
      payload.markup_percent = Math.round(selectedMarkup * 100) / 100;
    }

    // Include forex rate for international companies
    if (isInternational && data.fx_rate) {
      const forexRate = Number(data.fx_rate);
      if (!Number.isNaN(forexRate) && forexRate > 0) {
        payload.fx_rate = Math.round(forexRate * 100) / 100; // Ensure 2 decimal places
      }
    }

    if (isHourlyBudgetModel && dirtyFields?.estimated_total_hours) {
      const estimatedHours = Number(data.estimated_total_hours);
      if (!Number.isNaN(estimatedHours) && estimatedHours > 0) {
        payload.estimated_total_hours = estimatedHours;
      }
    }

    if (isHourlyBudgetModel && dirtyFields?.hours_per_day) {
      const submittedHoursPerDay = Number(data.hours_per_day);
      if (
        !Number.isNaN(submittedHoursPerDay) &&
        submittedHoursPerDay > 0 &&
        submittedHoursPerDay <= MAX_HOURS_PER_DAY
      ) {
        payload.hours_per_day = Math.round(submittedHoursPerDay * 100) / 100;
      }
    }

    return payload;
  };

  const submitSowUpdate = async (payload) => {
    const response = await httpClient.put(
      `api/v1/project-management/sows/${sow.id}/`,
      payload
    );

    if (response?.status === false) {
      throw new Error(response?.message || 'Failed to update SOW');
    }

    const updatedSow = response?.data || response;
    const successMessage = response?.message || 'SOW updated successfully';

    toast.success(successMessage);

    if (onSuccess) {
      onSuccess(updatedSow);
    }

    if (onClose) {
      onClose();
    }
  };

  const handleRevertToDraft = async () => {
    if (!sow?.id) {
      toast.error('SOW ID is missing');
      return;
    }

    setRevertLoading(true);

    try {
      const response = await httpClient.post(
        `/api/v1/project-management/sows/${sow.id}/revert-to-draft/`
      );

      if (response?.status === false) {
        throw new Error(
          response?.message || 'Failed to revert SOW to draft status'
        );
      }

      toast.success(response?.message || 'SOW reverted to DRAFT successfully');

      if (onSowUpdate) {
        onSowUpdate({
          status: 'draft',
          client_document: null,
          freelancer_document: null,
          document_context: {
            action: 'generate_sow',
            has_signed_sow: false,
            has_draft_sow: true,
            has_draft_addendum: false,
            draft_sow_id: sow?.id || null,
            draft_addendum_id: null,
          },
          ...(response?.data || {}),
        });
      }

      onDocumentsReset?.();

      setRevertDialogOpen(false);

      if (pendingFormData) {
        const payload = buildPayload(pendingFormData);
        await submitSowUpdate(payload);
        setPendingFormData(null);
      }
    } catch (error) {
      console.error('Error reverting SOW to draft:', error);
      toast.error(error?.message || 'Failed to revert SOW to draft status');
    } finally {
      setRevertLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = buildPayload(data);
      await submitSowUpdate(payload);
    } catch (error) {
      console.error('Error updating SOW:', error);

      const errorResponse = error?.response?.data || error?.data || error;
      const errorCode = errorResponse?.error_code || error?.error_code;
      const errorMessage = errorResponse?.message || error?.message;

      if (errorCode === 'SOW_ACTIVE_REVERT_REQUIRED') {
        setPendingFormData(data);
        setRevertDialogOpen(true);
        return;
      }

      if (errorCode === 'SOW_SIGNED_ADDENDUM_REQUIRED') {
        toast.error(errorMessage || 'Failed to update SOW');
        return;
      }

      toast.error(errorMessage || 'Failed to update SOW');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        width: '100%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflowY: 'auto',
        borderRadius: 2,
        p: 3,
      }}
    >
      <Typography variant='h4' sx={{ mb: 3, fontWeight: 600 }}>
        Edit SOW
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <InterestPreview
          selectedInterest={selectedInterest}
          markupPercent={markupPercentValue}
          fxRate={fxRate}
        />

        <FormControl fullWidth error={!!errors?.markup_percent} sx={{ mt: 3 }}>
          <FormLabel
            sx={{
              fontSize: '14px',
              marginBottom: 0.5,
              paddingLeft: 0.5,
              fontWeight: 500,
              color: '#4A5264',
            }}
          >
            Markup Percent (%)
          </FormLabel>
          <Controller
            name='markup_percent'
            control={control}
            render={({
              field: { value, onChange: fieldOnChange, onBlur, ...field },
            }) => (
              <NumberInput
                {...field}
                value={value}
                onChange={(nextValue) => {
                  if (nextValue === '') {
                    fieldOnChange('');
                    return;
                  }

                  // Handle string inputs to restrict decimal places
                  if (typeof nextValue === 'string') {
                    // Allow trailing decimal point for typing (e.g., "90.")
                    if (nextValue.endsWith('.')) {
                      fieldOnChange(nextValue);
                      return;
                    }

                    // Check for decimal point and count decimal places
                    const decimalIndex = nextValue.indexOf('.');
                    if (decimalIndex !== -1) {
                      const decimalPart = nextValue.substring(decimalIndex + 1);
                      // If more than 2 decimal places, truncate to 2
                      if (decimalPart.length > 2) {
                        const truncated = nextValue.substring(
                          0,
                          decimalIndex + 3
                        );
                        const numericValue = Number(truncated);
                        if (!Number.isNaN(numericValue)) {
                          // Validate range before allowing truncated value
                          if (numericValue >= 0 && numericValue <= 200) {
                            // Preserve the truncated string format (e.g., "90.00")
                            fieldOnChange(truncated);
                            return;
                          }
                          // If out of range, clamp it
                          const clamped = Math.max(
                            0,
                            Math.min(200, numericValue)
                          );
                          fieldOnChange(clamped);
                          return;
                        }
                        // If truncated value is invalid, don't update
                        return;
                      }
                    }
                  }

                  const numericValue = Number(nextValue);
                  if (Number.isNaN(numericValue)) {
                    return;
                  }

                  // Clamp value between 0 and 200
                  const clamped = Math.max(0, Math.min(200, numericValue));

                  // For string inputs with valid decimal format, preserve it
                  if (
                    typeof nextValue === 'string' &&
                    nextValue.includes('.')
                  ) {
                    const decimalIndex = nextValue.indexOf('.');
                    const decimalPart = nextValue.substring(decimalIndex + 1);
                    if (decimalPart.length <= 2) {
                      // Allow the string as-is if it has 2 or fewer decimal places
                      fieldOnChange(nextValue);
                      return;
                    }
                  }

                  // Round to 2 decimal places for numeric values
                  const rounded = Math.round(clamped * 100) / 100;
                  fieldOnChange(rounded);
                }}
                onBlur={(event) => {
                  onBlur(event);
                  const raw = event?.target?.value;
                  if (raw === undefined || raw === null || raw === '') {
                    return;
                  }

                  const numericValue = Number(raw);
                  if (Number.isNaN(numericValue)) {
                    return;
                  }

                  // Clamp value between 0 and 200
                  const clamped = Math.max(0, Math.min(200, numericValue));

                  // Round to 2 decimal places
                  const finalValue = Math.round(clamped * 100) / 100;
                  fieldOnChange(finalValue);
                  setValue('markup_percent', finalValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                placeholder='Enter markup percentage (0-200)'
                unit='%'
                inputProps={{ min: 0, max: 200, step: 0.01 }}
              />
            )}
          />
          {errors?.markup_percent && (
            <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
              {errors.markup_percent.message}
            </FormHelperText>
          )}
        </FormControl>

        {/* Forex Rate - Only for International companies */}
        {isInternational && (
          <FormControl fullWidth error={!!errors?.fx_rate} sx={{ mt: 3 }}>
            <FormLabel
              sx={{
                fontSize: '14px',
                marginBottom: 0.5,
                paddingLeft: 0.5,
                fontWeight: 500,
                color: '#4A5264',
              }}
            >
              Forex Rate (USD to INR)
            </FormLabel>
            <Controller
              name='fx_rate'
              control={control}
              render={({
                field: { value, onChange: fieldOnChange, onBlur, ...field },
              }) => (
                <NumberInput
                  {...field}
                  value={value}
                  onChange={(nextValue) => {
                    if (nextValue === '') {
                      fieldOnChange('');
                      return;
                    }

                    if (
                      typeof nextValue === 'string' &&
                      nextValue.endsWith('.')
                    ) {
                      fieldOnChange(nextValue);
                      return;
                    }

                    const numericValue = Number(nextValue);
                    if (Number.isNaN(numericValue) || numericValue < 0) {
                      return;
                    }

                    // Allow up to 2 decimal places
                    const rounded = Math.round(numericValue * 100) / 100;
                    fieldOnChange(rounded);
                  }}
                  onBlur={(event) => {
                    onBlur(event);
                    const raw = event?.target?.value;
                    if (raw === undefined || raw === null || raw === '') {
                      // Allow empty value - user intentionally cleared it
                      fieldOnChange('');
                      setValue('fx_rate', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      return;
                    }

                    const numericValue = Number(raw);
                    if (Number.isNaN(numericValue) || numericValue < 0) {
                      return;
                    }

                    // Round to 2 decimal places
                    const finalValue = Math.round(numericValue * 100) / 100;
                    fieldOnChange(finalValue);
                    setValue('fx_rate', finalValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  placeholder='Enter forex rate (e.g., 86.00)'
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
              )}
            />
            {errors?.fx_rate && (
              <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                {errors.fx_rate.message}
              </FormHelperText>
            )}
          </FormControl>
        )}

        {isHourlyBudgetModel && (
          <FormControl
            fullWidth
            error={!!errors?.estimated_total_hours}
            sx={{ mt: 3 }}
          >
            <FormLabel
              sx={{
                fontSize: '14px',
                marginBottom: 0.5,
                paddingLeft: 0.5,
                fontWeight: 500,
                color: '#4A5264',
              }}
            >
              Estimated Hours (optional)
            </FormLabel>
            <Controller
              name='estimated_total_hours'
              control={control}
              render={({
                field: { value, onChange: fieldOnChange, onBlur, ...field },
              }) => (
                <NumberInput
                  {...field}
                  value={value}
                  onChange={(nextValue) => {
                    if (nextValue === '') {
                      fieldOnChange('');
                      return;
                    }

                    const numericValue = Number(nextValue);
                    if (Number.isNaN(numericValue) || numericValue < 0) {
                      return;
                    }

                    fieldOnChange(Math.round(numericValue));
                  }}
                  onBlur={(event) => {
                    onBlur(event);
                    const raw = event?.target?.value;
                    if (raw === undefined || raw === null || raw === '') {
                      fieldOnChange('');
                      setValue('estimated_total_hours', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      return;
                    }

                    const numericValue = Number(raw);
                    if (Number.isNaN(numericValue) || numericValue < 0) {
                      return;
                    }

                    const finalValue = Math.round(numericValue);
                    fieldOnChange(finalValue);
                    setValue('estimated_total_hours', finalValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  placeholder='Enter estimated hours'
                  inputProps={{ min: 1, step: 1 }}
                />
              )}
            />
            {errors?.estimated_total_hours ? (
              <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                {errors.estimated_total_hours.message}
              </FormHelperText>
            ) : (
              <FormHelperText sx={{ fontSize: '0.75rem' }}>
                Optional. Leave unchanged to keep the current value. If omitted,
                the backend defaults to {DEFAULT_ESTIMATED_TOTAL_HOURS} hours.
                Maximum {MAX_ESTIMATED_TOTAL_HOURS} hours.
              </FormHelperText>
            )}
          </FormControl>
        )}

        {isHourlyBudgetModel && (
          <FormControl fullWidth error={!!errors?.hours_per_day} sx={{ mt: 3 }}>
            <FormLabel
              sx={{
                fontSize: '14px',
                marginBottom: 0.5,
                paddingLeft: 0.5,
                fontWeight: 500,
                color: '#4A5264',
              }}
            >
              Hours Per Day (optional)
            </FormLabel>
            <Controller
              name='hours_per_day'
              control={control}
              render={({
                field: { value, onChange: fieldOnChange, onBlur, ...field },
              }) => (
                <NumberInput
                  {...field}
                  value={value}
                  onChange={(nextValue) => {
                    if (nextValue === '') {
                      fieldOnChange('');
                      return;
                    }

                    if (
                      typeof nextValue === 'string' &&
                      nextValue.endsWith('.')
                    ) {
                      fieldOnChange(nextValue);
                      return;
                    }

                    const numericValue = Number(nextValue);
                    if (
                      Number.isNaN(numericValue) ||
                      numericValue < 0 ||
                      numericValue > MAX_HOURS_PER_DAY
                    ) {
                      return;
                    }

                    fieldOnChange(Math.round(numericValue * 100) / 100);
                  }}
                  onBlur={(event) => {
                    onBlur(event);
                    const raw = event?.target?.value;
                    if (raw === undefined || raw === null || raw === '') {
                      fieldOnChange('');
                      setValue('hours_per_day', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      return;
                    }

                    const numericValue = Number(raw);
                    if (
                      Number.isNaN(numericValue) ||
                      numericValue < 0 ||
                      numericValue > MAX_HOURS_PER_DAY
                    ) {
                      return;
                    }

                    const finalValue = Math.round(numericValue * 100) / 100;
                    fieldOnChange(finalValue);
                    setValue('hours_per_day', finalValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  placeholder='Enter daily working hours'
                  inputProps={{ min: 1, max: MAX_HOURS_PER_DAY, step: 0.25 }}
                />
              )}
            />
            {errors?.hours_per_day ? (
              <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.75rem' }}>
                {errors.hours_per_day.message}
              </FormHelperText>
            ) : (
              <FormHelperText sx={{ fontSize: '0.75rem' }}>
                Optional. Leave unchanged to keep the current value or default
                to the freelancer&apos;s available hours per day
                {freelancerDefaultHoursPerDay
                  ? ` (${freelancerDefaultHoursPerDay}h).`
                  : '.'}
              </FormHelperText>
            )}
          </FormControl>
        )}

        <Box display='flex' justifyContent='flex-end' gap={2} marginTop={4}>
          <Button variant='text' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Updating SOW...' : 'Update SOW'}
          </Button>
        </Box>
      </form>

      <RevertToDraftDialog
        open={revertDialogOpen}
        title='Edit SOW (Revert to Draft)'
        description='To edit this SOW, it must be reverted to draft.'
        note='This action will delete all existing documents.'
        loading={revertLoading}
        onCancel={() => {
          if (!revertLoading) {
            setRevertDialogOpen(false);
            setPendingFormData(null);
          }
        }}
        onProceed={handleRevertToDraft}
      />
      <SowAddendumRequiredDialog
        open={addendumRequiredOpen}
        onCancel={() => setAddendumRequiredOpen(false)}
        onProceed={() => {
          setAddendumRequiredOpen(false);
          setAddendumFormOpen(true);
        }}
      />
      <SowAddendumFormDialog
        open={addendumFormOpen}
        onClose={() => setAddendumFormOpen(false)}
      />
    </Paper>
  );
};

export default SOWEditForm;
